import { Model, ModelId } from "@/lib/types"
import { FormattedRequestObject } from "@/lib/types"
import { models } from "@/lib/models/model-list"
import { encodingForModel } from "js-tiktoken"

// Core types for pricing calculations
export interface TokenUsage {
    inputTokens: number
    outputTokens: number
    totalTokens: number
}

export interface ImageUsage {
    inputMegapixels: number
    outputMegapixels: number
    totalMegapixels: number
    imageCount?: number
}

export interface CostBreakdown {
    inputCost: number
    outputCost: number
    totalCost: number
}

export interface PricingInput {
    model: Model
    usage?: Partial<TokenUsage & ImageUsage>
    textContent?: string
    apiResponse?: any
    applyProfitMultiplier?: boolean
}

export interface PricingResult extends TokenUsage, ImageUsage, CostBreakdown {
    model: Model
}





import { getModelTier, MODEL_TIERS } from '@/lib/models/model-tiers'

// Map model tiers to profit multipliers
export const MODEL_TIER_MULTIPLIERS = {
    [MODEL_TIERS.PREMIUM]: 1.3,
    [MODEL_TIERS.STANDARD]: 1.6,
    [MODEL_TIERS.BASIC]: 2
}



export function getProfitMultiplier(model: Model): number {
    // Get the model tier using the centralized function
    const tier = getModelTier(model)

    // Return the appropriate multiplier for this tier
    return MODEL_TIER_MULTIPLIERS[tier]
}


/**
 * Core pricing calculation engine
 * Handles all pricing types and scenarios in one place
 */
export class PricingCore {
    /**
     * Count tokens for text using the specified model's tokenizer
     */
    static estimateTokenUsage(text: string, modelId: string = "gpt-4"): number {
        try {
            const encoding = encodingForModel("gpt-4")
            const tokens = encoding.encode(text)
            return tokens.length
        } catch (error) {
            return Math.ceil(text.length / 4)
        }
    }


    static getProfitMultiplier(model: Model): number {
        // Get the model tier using the centralized function
        const tier = getModelTier(model)

        // Return the appropriate multiplier for this tier
        return MODEL_TIER_MULTIPLIERS[tier]
    }

    
    /**
     * Calculate token-based costs
     */
    static calculateTokenCosts(model: Model, tokenUsage: TokenUsage): Partial<CostBreakdown> {
        const inputPrice = model.pricing.input_token || 0
        const outputPrice = model.pricing.output_token || 0

        const inputCost = tokenUsage.inputTokens * inputPrice
        const outputCost = tokenUsage.outputTokens * outputPrice

        return {
            inputCost,
            outputCost,
            totalCost: inputCost + outputCost
        }
    }

    /**
         * Main pricing calculation method
         */
    static calculate(input: PricingInput): PricingResult {
        const { model, usage = {}, textContent, apiResponse, applyProfitMultiplier = true } = input

        // Initialize result with defaults
        const result: PricingResult = {
            model,
            inputTokens: usage.inputTokens || 0,
            outputTokens: usage.outputTokens || 0,
            totalTokens: usage.totalTokens || 0,
            inputMegapixels: usage.inputMegapixels || 0,
            outputMegapixels: usage.outputMegapixels || 0,
            totalMegapixels: usage.totalMegapixels || 0,
            imageCount: usage.imageCount || 0,
            inputCost: 0,
            outputCost: 0,
            totalCost: 0
        }

        // If we have an API response with usage data, prioritize that
        if (apiResponse?.usage) {
            result.inputTokens = apiResponse.usage.prompt_tokens || 0
            result.outputTokens = apiResponse.usage.completion_tokens || 0
            result.totalTokens = apiResponse.usage.total_tokens || (result.inputTokens + result.outputTokens)

            // Use API-provided cost if available (most accurate)
            if (typeof apiResponse.usage.cost === 'number') {
                result.totalCost = apiResponse.usage.cost

                // Try to split input/output costs proportionally for analytics
                const tokenCosts = this.calculateTokenCosts(model, result)
                const calculatedTotal = tokenCosts.totalCost || 0

                if (calculatedTotal > 0) {
                    const inputRatio = (tokenCosts.inputCost || 0) / calculatedTotal
                    const outputRatio = (tokenCosts.outputCost || 0) / calculatedTotal
                    result.inputCost = result.totalCost * inputRatio
                    result.outputCost = result.totalCost * outputRatio
                } else {
                    result.inputCost = 0
                    result.outputCost = result.totalCost
                }
            } else {
                // Calculate from our pricing data
                const tokenCosts = this.calculateTokenCosts(model, result)
                result.inputCost = tokenCosts.inputCost || 0
                result.outputCost = tokenCosts.outputCost || 0
                result.totalCost = tokenCosts.totalCost || 0
            }
        }

        // If we have text content but no token counts, estimate them
        if (textContent && !result.inputTokens) {
            result.inputTokens = this.estimateTokenUsage(textContent, model.id)
            result.totalTokens = result.inputTokens + result.outputTokens

            const tokenCosts = this.calculateTokenCosts(model, result)
            result.inputCost += tokenCosts.inputCost || 0
            result.outputCost += tokenCosts.outputCost || 0
            result.totalCost += tokenCosts.totalCost || 0
        }

        // Calculate token costs if we have token counts but no costs yet
        if ((result.inputTokens || result.outputTokens) && result.totalCost === 0) {
            const tokenCosts = this.calculateTokenCosts(model, result)
            result.inputCost = tokenCosts.inputCost || 0
            result.outputCost = tokenCosts.outputCost || 0
            result.totalCost = tokenCosts.totalCost || 0
        }

        // Handle image costs from API response
        if (apiResponse?.apiResponse?.images) {
            const imageMegapixels = apiResponse.apiResponse.images.reduce((total: number, image: any) => {
                return total + (image.width * image.height) / 1_000_000
            }, 0)
            
            result.outputMegapixels = imageMegapixels
            result.imageCount = apiResponse.apiResponse.images.length
        }

        // Calculate image costs if we have image data
        if (result.outputMegapixels || result.imageCount) {
            const imageCosts = this.calculateImageCosts(model, result)
            result.outputCost += imageCosts.outputCost || 0
            result.totalCost += imageCosts.totalCost || 0
        }

        // Update total megapixels
        result.totalMegapixels = result.inputMegapixels + result.outputMegapixels

        // Apply profit multiplier if requested
        if (applyProfitMultiplier) {
            const multiplier = getProfitMultiplier(model)
            result.inputCost *= multiplier
            result.outputCost *= multiplier
            result.totalCost *= multiplier
        }

        return result
    }




    /**
     * Calculate image-based costs
     */
    static calculateImageCosts(model: Model, imageUsage: ImageUsage): Partial<CostBreakdown> {
        let imageCost = 0

        // Handle different image pricing models
        if (model.pricing.output_image_mp && imageUsage.outputMegapixels) {
            imageCost = imageUsage.outputMegapixels * model.pricing.output_image_mp
        } else if (model.pricing.output_image && imageUsage.imageCount) {
            imageCost = imageUsage.imageCount * model.pricing.output_image
        } else if (model.pricing.request && imageUsage.imageCount) {
            imageCost = imageUsage.imageCount * model.pricing.request
        }

        return {
            outputCost: imageCost,
            totalCost: imageCost
        }
    }



    /**
     * Estimate usage from credits (for UI display)
     */
    static estimateUsageFromCredits(
        model: Model,
        credits: number
    ): { count: number, unit: 'messages' | 'images' | 'unknown' } | null {
        const creditsInDollars = credits / 1000
        const { architecture, pricing } = model

        // Try text calculation first
        if (architecture.outputModalities.includes('text') && (pricing.input_token || pricing.output_token)) {
            const inputTokensPerMessage = 1000
            const outputTokensPerMessage = 300

            const inputCostPerToken = pricing.input_token || 0
            const outputCostPerToken = pricing.output_token || 0

            const inputCostPerMessage = (inputTokensPerMessage * inputCostPerToken)
            const outputCostPerMessage = (outputTokensPerMessage * outputCostPerToken)
            const messageCostInDollars = inputCostPerMessage + outputCostPerMessage

            if (messageCostInDollars <= 0) return { count: Infinity, unit: 'messages' }

            const messageCount = Math.floor(creditsInDollars / messageCostInDollars)
            return { count: messageCount, unit: 'messages' }
        }

        // Try image calculation
        if (architecture.outputModalities.includes('image')) {
            let costPerImageInDollars = 0

            if (pricing.request) {
                costPerImageInDollars = pricing.request
            } else if (pricing.output_image) {
                costPerImageInDollars = pricing.output_image
            } else if (pricing.output_image_mp) {
                costPerImageInDollars = pricing.output_image_mp // Assume 1 megapixel
            }

            if (costPerImageInDollars <= 0) return { count: Infinity, unit: 'images' }

            const imageCount = Math.floor(creditsInDollars / costPerImageInDollars)
            return { count: imageCount, unit: 'images' }
        }

        return { count: 0, unit: 'unknown' }
    }

    /**
     * Get model by ID with fallback
     */
    static getModel(modelId: ModelId): Model | null {
        return models[modelId] || null
    }

    static calculateMaxOutputTokens(userBalance: number, estimatedInputCost: number, model: Model): number {
        const outputPrice = model.pricing.output_token || 0
        if (outputPrice === 0) return Infinity
        const remainingBalance = userBalance - estimatedInputCost
        const maxTokens = Math.floor(remainingBalance / (outputPrice * (this.getProfitMultiplier(model) || 1)))
        return maxTokens
    }

    static checkSufficientBalance(estimatedCost: number, userBalance: number, threshold = -0.2): boolean {
        return userBalance - estimatedCost >= threshold
    }

    // duplicate removed
}
